import React from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, Sparkles, Heart, Wallet, Compass, Clock, MessageCircle, Search as SearchIcon, Anchor,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/i18n";

export default function ComeFunziona() {
  const navigate = useNavigate();
  const { lang } = useI18n();
  const IT = lang === "it";

  return (
    <div data-testid="come-funziona-page" className="bg-white">
      {/* HERO MANIFESTO */}
      <section className="relative pt-32 pb-20 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-sand via-white to-white" />
        <div className="max-w-5xl mx-auto px-5 lg:px-10">
          <p className="label-eyebrow">{IT ? "Il nostro manifesto" : "The Seamatch philosophy"}</p>
          <h1 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-7xl text-navy tracking-tight leading-[1.04]">
            {IT
              ? "Seamatch non nasce per trovare una vacanza in barca."
              : "Seamatch is not a booking portal."}
            <br />
            <em className="italic text-gold font-medium">
              {IT
                ? "Nasce per costruire la vacanza giusta per te."
                : "It is a private yacht concierge."}
            </em>
          </h1>
          <p className="mt-7 max-w-2xl text-base lg:text-lg text-ink leading-relaxed">
            {IT
              ? "Niente pacchetti standard. Niente cataloghi infiniti da filtrare. Seamatch è un sistema intelligente che parte da te — dal tuo budget, dal tuo gruppo, dal tuo modo di vivere il mare — e cuce su misura la proposta più sensata. Anche quando non hai ancora le idee chiare."
              : "We do not publish catalogues, lists or filters. We listen, we design, we curate. Every Seamatch holiday is hand-built by a small team of specialists who personally know the Mediterranean and the charter partners we work with."}
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Button
              data-testid="cf-cta-primary"
              onClick={() => navigate("/search")}
              className="bg-navy text-white hover:bg-[#06101f] rounded-full h-12 px-7 text-sm font-semibold"
            >
              <SearchIcon className="h-4 w-4 mr-2" /> {IT ? "Inizia la tua richiesta" : "Brief my concierge"}
            </Button>
            <Button
              data-testid="cf-cta-secondary"
              onClick={() => navigate("/contatti")}
              variant="outline"
              className="rounded-full h-12 px-7 text-sm font-semibold border-navy/30 text-navy hover:bg-sand"
            >
              {IT ? "Parla con un consulente" : "Speak with a specialist"} <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="py-20 lg:py-24 border-t border-[#E2E8F0]">
        <div className="max-w-6xl mx-auto px-5 lg:px-10">
          <p className="label-eyebrow">{IT ? "Cosa ci rende diversi" : "What we believe in"}</p>
          <h2 className="mt-3 font-serif text-3xl lg:text-5xl text-navy tracking-tight max-w-3xl">
            {IT ? "Quattro principi, una sola promessa." : "Four principles. One promise."}
          </h2>

          <div className="mt-12 grid md:grid-cols-2 gap-6">
            {[
              {
                icon: Wallet,
                eyebrow: IT ? "01 — Accessibilità" : "01 — Curation",
                title: IT ? "La vacanza in barca non è più per pochi." : "A vetted circle of charter partners.",
                body: IT
                  ? "Per molti la vacanza in barca sembra un sogno riservato. Noi cominciamo proprio dal tuo budget e dalle tue esigenze: anche cifre contenute aprono opzioni reali, soprattutto se sei flessibile su date o destinazione. Te lo diciamo con trasparenza."
                  : "We work with a deliberately small network of charter companies across the Mediterranean — selected over years, personally known to our team. Every yacht, every crew, every itinerary is one we would happily recommend to a close friend.",
              },
              {
                icon: Heart,
                eyebrow: IT ? "02 — Personalizzazione" : "02 — Personalisation",
                title: IT ? "Nessun pacchetto preconfezionato." : "Designed around your party.",
                body: IT
                  ? "Ogni viaggio è diverso. Analizziamo budget, periodo, numero di persone, esperienza desiderata, destinazioni preferite e tipo di barca. Da questi dati nasce una proposta cucita su misura — non una lista filtrata."
                  : "Couples, families, friends, private celebrations — every holiday is shaped around the people on board. Itinerary, crew style, provisioning, on-shore experiences: details are tailored quietly, one conversation at a time.",
              },
              {
                icon: Compass,
                eyebrow: IT ? "03 — Richiesta guidata" : "03 — Stress-free planning",
                title: IT ? "Pochi passaggi, mai un labirinto." : "One concierge. One point of contact.",
                body: IT
                  ? "Bastano 2 minuti. Il questionario ti accompagna passo dopo passo: budget, gruppo, mete, date, esperienza. Ovunque puoi rispondere 'Consigliami tu' — non un dato mancante ma un'informazione preziosa per i nostri consulenti."
                  : "From your first message to the moment you step on board, you speak with the same specialist. We coordinate the charter contract, the skipper, the provisioning and every small request along the way.",
              },
              {
                icon: Sparkles,
                eyebrow: IT ? "04 — Match personalizzato" : "04 — Mediterranean expertise",
                title: IT ? "Proposte già pensate per te." : "Local knowledge, quietly applied.",
                body: IT
                  ? "Quando arriva la tua richiesta, analizziamo le disponibilità reali, individuiamo le migliori soluzioni e ti scriviamo entro 24 ore con i preventivi più adatti. Se troviamo alternative più convenienti o più calzanti, te le proponiamo — perché spesso la scelta migliore non è la prima che si immagina."
                  : "From the Aeolian Islands to the Greek Cyclades, from Sardinia to the Dalmatian coast — our advisors know the harbours, the seasonal winds, the tables worth booking and the anchorages worth waking up in. That knowledge shapes every proposal.",
              },
            ].map((c, i) => (
              <article key={i} data-testid={`cf-pillar-${i}`} className="rounded-2xl bg-white border border-[#E2E8F0] p-8">
                <span className="inline-flex h-10 w-10 rounded-full items-center justify-center bg-sand">
                  <c.icon className="h-5 w-5 text-navy" />
                </span>
                <p className="mt-5 label-eyebrow">{c.eyebrow}</p>
                <h3 className="mt-3 font-serif text-2xl text-navy leading-tight">{c.title}</h3>
                <p className="mt-3 text-sm text-ink leading-relaxed">{c.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* TIMELINE — FLUSSO */}
      <section className="py-20 lg:py-24 bg-sand">
        <div className="max-w-6xl mx-auto px-5 lg:px-10">
          <p className="label-eyebrow">{IT ? "Il flusso, in chiaro" : "How a Seamatch holiday takes shape"}</p>
          <h2 className="mt-3 font-serif text-3xl lg:text-5xl text-navy tracking-tight">
            {IT ? "Quattro passi. Zero stress." : "Four quiet steps. Curated by experts."}
          </h2>

          <ol className="mt-12 space-y-10 lg:space-y-14">
            {[
              { i: 1, ic: SearchIcon,
                t: IT ? "Ci racconti il tuo viaggio (2 minuti)" : "Share a brief with your concierge",
                d: IT
                  ? "Budget, date, gruppo, mete preferite, tipo di esperienza. Niente registrazioni."
                  : "A short, guided conversation about your party, your preferred regions, the experience you have in mind. No account, no clutter." },
              { i: 2, ic: Compass,
                t: IT ? "Costruiamo la proposta su misura" : "We design your itinerary",
                d: IT
                  ? "Confrontiamo le disponibilità reali di partner selezionati e incrociamo i dati con il tuo profilo per individuare le migliori soluzioni e le alternative più sensate."
                  : "Your specialist quietly reaches out to a short list of trusted charter partners and hand-builds a single, fitting proposal — yacht, route and crew style included." },
              { i: 3, ic: MessageCircle,
                t: IT ? "Ti scriviamo entro 24 ore" : "Receive your personal proposal",
                d: IT
                  ? "Email e WhatsApp con i preventivi che davvero meritano la tua attenzione — comprese eventuali alternative più convenienti o più calzanti."
                  : "Within 24 hours your concierge presents the proposal by email and WhatsApp, available to refine any detail with you — pace, harbours, on-board service." },
              { i: 4, ic: Anchor,
                t: IT ? "Scegli e parti" : "Step on board",
                d: IT
                  ? "Quando approvi una proposta, ti accompagniamo fino alla conferma. Nessuna fretta, nessun obbligo, nessuna commissione nascosta."
                  : "Once confirmed, we coordinate every detail with the charter partner. From provisioning to boarding day, your concierge stays one message away." },
            ].map((s) => (
              <li key={s.i} data-testid={`cf-step-${s.i}`} className="grid md:grid-cols-12 gap-6 items-start">
                <div className="md:col-span-2">
                  <span className="font-serif text-6xl text-gold/80 leading-none">0{s.i}</span>
                </div>
                <div className="md:col-span-10">
                  <div className="flex items-center gap-3">
                    <span className="h-9 w-9 rounded-full bg-navy text-white flex items-center justify-center"><s.ic className="h-4 w-4" /></span>
                    <h3 className="font-serif text-2xl text-navy">{s.t}</h3>
                  </div>
                  <p className="mt-3 text-ink leading-relaxed max-w-2xl">{s.d}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="py-20 lg:py-24">
        <div className="max-w-5xl mx-auto px-5 lg:px-10 text-center">
          <p className="label-eyebrow">{IT ? "La nostra promessa" : "Our promise"}</p>
          <h2 className="mt-4 font-serif text-3xl lg:text-5xl text-navy tracking-tight leading-tight">
            {IT
              ? "Costruiamo la vacanza in barca attorno a te — non il contrario."
              : "A holiday designed around you. Quietly. Precisely. By people who know the Mediterranean."}
          </h2>
          <p className="mt-6 max-w-2xl mx-auto text-ink leading-relaxed">
            {IT
              ? "Tu non devi adattarti a un pacchetto. Sono le proposte ad adattarsi a te: al tuo budget, ai tuoi tempi, al tuo modo di vivere il mare."
              : "Every Seamatch holiday is hand-built around you — your party, your itinerary, your preferred pace at sea. No two proposals are ever the same."}
          </p>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 lg:py-24 bg-navy text-white">
        <div className="max-w-4xl mx-auto px-5 lg:px-10 text-center">
          <Clock className="h-6 w-6 mx-auto text-gold" />
          <h2 className="mt-5 font-serif text-3xl lg:text-5xl tracking-tight leading-tight">
            {IT ? "Pronto a iniziare?" : "Begin your Mediterranean holiday."}
          </h2>
          <p className="mt-5 max-w-xl mx-auto text-white/80 leading-relaxed">
            {IT
              ? "Bastano due minuti. Nessuna registrazione. Risposta entro 24 ore."
              : "Share a short brief with your concierge. We will return with a hand-built proposal within 24 hours."}
          </p>
          <Button
            data-testid="cf-final-cta"
            onClick={() => navigate("/search")}
            className="mt-9 bg-gold text-navy hover:bg-[#c89e29] rounded-full h-12 px-8 text-sm font-semibold"
          >
            {IT ? "Trova la mia vacanza" : "Speak with my concierge"} <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </section>
    </div>
  );
}
